<template>
  <li
    :class="{ pikpak: isPikpak, h5: isMobile }"
    class="card-container"
  >
    <div class="card-header">
      <img
        :alt="headerName"
        :src="headerSvg"
        class="card-svg"
        height="54"
        width="54"
      >
      <h3>{{ headerName }}</h3>
      <div
        v-if="isPikpak && discount"
        class="save"
      >
        <img
          :src="PriceSaveSVG"
          alt=""
          height="80"
          width="74"
        >
        <div class="save-detail">
          <span class="save-content">SAVE</span>
          <div class="percent">
            <span class="percent-front">{{ discount }}</span>
            <span class="percent-end">%</span>
          </div>
        </div>
      </div>
    </div>
    <div class="card-main">
      <div class="card-price">
        <span
          :data-unit="priceUnit"
          class="currency-type"
        >{{ priceUnit || $t('currency-type') }}</span>
        <span class="value">{{ priceValue }}</span>
        <span class="capacity">TB/{{ $t('month') }}</span>
      </div>
    </div>
    <div class="card-footer">
      <div
        v-if="isPikpak"
        class="cloud-add"
      >
        <img
          :alt="$t('home-price-card-cloud-speed-add')"
          :src="CorrectSVG"
          height="24"
          width="24"
        >
        <span class="cloud-text">{{ $t('home-price-card-cloud-speed-add') }}</span>
      </div>
      <div
        v-else
        class="cloud-add"
      >
        <img
          :alt="$t('home-price-card-cloud-add')"
          :src="WrongSVG"
          height="24"
          width="24"
        >
        <span class="cloud-text">{{ $t('home-price-card-cloud-add') }}</span>
      </div>
      <a
        :href="linkUrl"
        class="detail"
        rel="noopener noreferrer"
        target="_blank"
        @click="trackMainPageClick(trackName)"
      >{{ $t('home-price-card-detail') }}</a>
    </div>
  </li>
</template>

<script
  lang="ts"
  setup
>
import { computed } from '#imports'
import CorrectSVG from '@/assets/images/home/correct.svg'
import PriceSaveSVG from '@/assets/images/home/price-save.svg'
import WrongSVG from '@/assets/images/home/wrong.svg'
import { useLayout } from '@/store/layout'
import $track from '@/track/webOfficialWebsite'

type ButtonTrackNameType = Parameters<typeof $track.web_official_main_page_click>[0]['button'];

defineProps<{
  headerSvg: string;
  headerName: string;
  isPikpak: boolean;
  priceValue: string;
  priceUnit?: string
  linkUrl: string;
  trackName: ButtonTrackNameType;
  discount?: number | string
}>()

const isMobile = computed(() => useLayout().isMobile)

const trackMainPageClick = (type: ButtonTrackNameType) => {
  $track.web_official_main_page_click({ button: type })
}
</script>

<style
  lang="less"
  scoped
>
@import '@/assets/styles/util';

.card {
  &-container {
    width: min(340px, 22.7vw); // 340/1200*0.8
    border-radius: 18px;
    background: #fff;
    padding: 24px;
  }

  &-header {
    display: flex;
    align-items: center;
    padding-left: 10px;

    h3 {
      font-weight: 700;
      font-size: 22px;
      line-height: 26px;
      margin-left: 12px;
      color: #000;
    }
  }

  &-price {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: min(38px, 2.53vw); // 38/1200*0.8
    line-height: 22px;
    color: #000;

    .currency-type {
      font-weight: 600;
      font-size: 24px;
      line-height: 22px;
    }

    .value {
      font-weight: 600;
      font-size: 54px;
      margin-top: min(34px, 2.361vw); // 34/1440
      white-space: nowrap;
    }

    .capacity {
      font-weight: 400;
      font-size: 24px;
      margin-top: min(54px, 3.75vw); // 54/1440
      line-height: 22px;
    }
  }

  &-footer {
    margin-top: min(56px, 3.89vw); // 56/1440
    display: flex;
    flex-direction: column;
    align-items: center;
    font-weight: 400;
    line-height: 22px;
    color: #ff663b;

    .cloud-add {
      background-color: #f2f5fa;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      border-radius: 4px;
      width: 100%;
      padding: 7px 0px;

      img {
        margin-right: 8px;
      }
    }

    .cloud-text {
      font-size: 14px;
    }

    .detail {
      line-height: 17px;
      margin-top: 16px;
      font-size: 14px;
      color: #666;
    }
  }
}

.pikpak {
  &.card-container {
    width: min(400px, 33.3vw);
    background: url('@/assets/images/home/price-pikpak-card-bg.svg');
    border-radius: 16px;
  }

  .card {
    &-header {
      position: relative;

      h3 {
        color: #fff;
      }

      .save {
        display: flex;
        justify-content: center;
        position: absolute;
        top: 0;
        right: 0;
        font-weight: 700;
        font-size: 14px;
        line-height: 26px;
        color: #fff;
        transform: translate(0px, -31px);

        img {
          position: relative;
        }

        &-detail {
          position: absolute;
          top: 0;

          .save-content {
            display: block;
            text-align: center;
          }

          .percent {
            transform: translate(3px, 2px);

            &-front {
              font-size: 42px;
            }

            &-end {
              font-size: 16px;
            }
          }
        }
      }
    }

    &-price {
      color: #fff;
      margin-top: min(58px, 4.03vw);

      .value {
        font-size: 76px;
      }

      .capacity {
        margin-top: min(72px, 5.14vw);
      }
    }

    &-footer {
      margin-top: min(86px, 6.14vw);

      .cloud-add {
        background: rgb(98, 122, 255);
        color: #fff;
      }

      .detail {
        color: #fff;
      }
    }
  }
}

@media screen and (min-width: @mobile-break-width) and (max-width: @pc-min-width) {
  .card {
    &-container {
      width: 284px;
      height: 354px;
      padding: 12px;
    }

    &-price {
      margin-top: 9.5px;

      .value {
        font-size: 54px;
        line-height: 22px;
      }

    }

    &-footer {
      margin-top: 26px;

      .cloud-add {
        padding: 6px 0px;
      }

      .detail {
        margin-top: 8px;
      }
    }
  }

  .pikpak {
    border-radius: 14px;

    &.card-container {
      width: 325px;
      height: 406px;
    }

    .card {
      &-header {
        padding-left: 6px;

        .save {
          transform: translate(0px, -18px);
        }
      }

      &-price {
        margin-top: 30px;

        .value {
          font-size: 60px;
          line-height: 14px;
        }

        .capacity {
          margin-top: 72px;
        }
      }

      &-footer {
        margin-top: 44px;

        .cloud-add {
          padding: 8px 0px;
        }
      }
    }
  }
}

.h5 {
  &.pikpak {
    .card {
      &-header {
        h3 {
          font-size: 13.619px;
          line-height: 16px;
          margin-left: 7px;
        }

        .save {
          img {
            width: 40.52px;
            height: 43.81px;
          }

          &-content {
            font-size: 8px;
            line-height: 12px;
          }

          .percent {
            &-front {
              font-size: 23px;
              line-height: 14px;
            }

            &-end {
              font-size: 9px;
              line-height: 14px;
            }
          }
        }
      }
    }
  }

  &.card-container {
    width: 230px;
    height: 288px;
    padding: 16px 0px;
    border-radius: 12px;
    margin: auto;
  }

  .card {

    &-header {
      padding-left: 16px;
      line-height: 17px;

      .save {
        transform: translate(-13px, -22px);
      }

      img {
        width: 34px;
        height: 34px;
      }

      h3 {
        font-size: 13.8251px;
        line-height: 17px;
        margin-left: 8px;
      }
    }

    &-price {
      margin-top: 23.5px;
      line-height: 14px;

      .currency-type {
        font-size: 20px;
      }

      .value {
        font-size: 46px;
        line-height: 14px;
        margin-top: 32px;
      }

      .capacity {
        font-size: 16px;
        margin-top: 32px;
      }
    }

    &-footer {
      margin-top: 32px;
      line-height: 14px;

      .cloud-add {
      }

      .cloud-add {
        width: 198px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;

        img {
          width: 16px;
          height: 16px;
          margin-right: 4px;
        }
      }

      .detail {
        margin-top: 10px;
        font-size: 12px;
      }
    }
  }
}
</style>

<style lang="less">
.price-carousel {
  .el-carousel__item.is-active {
    // transform: scale(1.2);
    .card-container {
      box-shadow: 0px 0px 6px rgb(0 0 0 / 10%);
    }
  }
}
</style>
